import md5 from 'md5'

import { Ga4Item, queueAnalyticsEvent, SeoEvent } from 'lib/gtm'
import { CustomerTypes, isCustomerAdult } from 'modules/customer'
import {
  SeoLastSelectedProductDataStorage,
  SeoListName,
  SeoPromotion
} from 'shared/analytics'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import { ProductTypes } from './types/products'
import { getProductUnitDetails,isNewProduct } from './utils/products'
import { formatPriceWithoutCurrency } from './utils/quantity'

interface viewProductListProps {
  user: CustomerTypes | undefined
  products: ProductTypes[]
  listName: SeoListName
}

export const viewProductList = ({
  user,
  products,
  listName
}: viewProductListProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  const items = collectItems(products, listName)
  const eventData = {
    event: SeoEvent.VIEW_ITEM_LIST, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    ecommerce: {
      listName: listName,
      items
    }
  }

  queueAnalyticsEvent(eventData)
}

interface selectProductProps {
  user: CustomerTypes | undefined
  product: ProductTypes
  idxOnList: number
  listName: SeoListName
}

export const selectProduct = ({
  user,
  product,
  idxOnList,
  listName
}: selectProductProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  SeoLastSelectedProductDataStorage.set(product, listName)

  const promotion = SeoPromotion.get()
  const eventData = {
    event: SeoEvent.SELECT_ITEM, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    ecommerce: {
      value: formatPriceWithoutCurrency(
        product?.pricing?.priceGross?.value || 0
      ),
      currency:
        product.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined,
      items: [
        {
          item_name: product.nameLong || undefined,
          item_id: product.code || undefined,
          price: formatPriceWithoutCurrency(
            product?.specialOfferPricing?.priceGross?.value ||
              product?.pricing?.priceGross.value ||
              0
          ),
          discount: product.specialOfferPricing
            ? formatPriceWithoutCurrency(
                product?.pricing?.priceGross?.value ||
                  0 - product?.specialOfferPricing?.priceGross?.value ||
                  0
              )
            : 0,
          currency:
            product?.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined,
          item_brand: product.brand?.name || undefined,
          item_promotion: product.specialOfferPricing ? 'yes' : 'no',
          item_available: product.isAvailable ? 'yes' : 'no',
          item_new: isNewProduct(product.tags) ? 'yes' : 'no',
          item_adult_only:
            product.ageLimit && product.ageLimit >= 18 ? 'yes' : 'no',
          index: idxOnList,
          ...getProductCategoriesForSeo(product),
          item_variant: getProductUnitDetails(product) || undefined,
          item_list_name: listName,
          promotion_id: promotion?.promotionId || undefined,
          creative_name: promotion?.creativeName || undefined,
          quantity: 1
        }
      ]
    }
  }

  queueAnalyticsEvent(eventData)
}

interface viewProductProps {
  user: CustomerTypes | undefined
  product: ProductTypes
  listName: SeoListName
  ecommerce: {
    value: number
    currency: string
    coupon?: string
  }
}

export const viewProduct = ({
  user,
  product,
  ecommerce,
  listName
}: viewProductProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  const lastSelectedProductData = SeoLastSelectedProductDataStorage.get()

  const eventData = {
    event: SeoEvent.VIEW_ITEM, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    ecommerce: {
      ...ecommerce,
      items: [
        {
          item_name: product?.nameLong || undefined, //product name
          item_id: product?.code || undefined, // product id - it should be the same as you have in google merchant center and facebook catalog feed
          price:
            formatPriceWithoutCurrency(
              product?.specialOfferPricing?.priceGross?.value ||
                product?.pricing?.priceGross?.value ||
                0
            ) || undefined, // 1 item price (with applied discount - if exist)
          discount: product?.specialOfferPricing
            ? formatPriceWithoutCurrency(
                product?.pricing?.priceGross?.value ||
                  0 - product?.specialOfferPricing?.priceGross?.value ||
                  0
              )
            : 0, // The monetary discount value associated with the 1 item. - if no discount 0
          currency:
            product?.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined, //currency
          item_brand: product?.brand?.name || undefined, // product manufacturer
          item_promotion: product?.specialOfferPricing ? 'yes' : 'no', // yes if promotion applied, no if no promotion
          item_available: product?.isAvailable ? 'yes' : 'no', // yes if item available, no if not
          item_new: isNewProduct(product?.tags || []) ? 'yes' : 'no', // yes if item new (nowość) in shop, no if not new product
          item_adult_only:
            product?.ageLimit && product?.ageLimit >= 18 ? 'yes' : 'no', // yes if item for clients 18+, no if for everyone
          index: 0, //product position on the list - starts from 0
          ...getProductCategoriesForSeo(product), //Category path corresponding to the one on the page - if there are more steps in path add another item_category2 etc. (MAX number item_category5)
          item_variant: product ? getProductUnitDetails(product) : undefined, //variant of the product
          item_list_name: lastSelectedProductData?.listName || listName, //list name where product displayed
          creative_name:
            lastSelectedProductData?.promotion?.creativeName || undefined,
          promotion_id:
            lastSelectedProductData?.promotion?.promotionId || undefined,
          quantity: 1 //number of pieces - default value 1
        }
      ]
    }
  }

  queueAnalyticsEvent(eventData)
}

interface addToWishlistSeoProps {
  user: CustomerTypes | undefined
  product: ProductTypes
  button_place?: string
  idxOnList: number
  coupon?: string
  listName: SeoListName
}

export const addToWishlistSeo = ({
  user,
  product,
  button_place,
  idxOnList,
  coupon,
  listName
}: addToWishlistSeoProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  const promotion = SeoPromotion.get()
  const eventData = {
    event: SeoEvent.ADD_TO_WISHLIST, //constant
    type: 'ecommerce', //constant
    user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
    user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
    button_place: button_place || undefined, //button place where product was added to whishlist. It can be like: cart page, product page, product page - list, category page, profile page, etc.
    ecommerce: {
      value: formatPriceWithoutCurrency(
        product?.pricing?.priceGross?.value || 0
      ), //total value paid (with taxes, shipping, etc)
      currency:
        product?.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined, //currency,
      coupon, //coupon if exists - if not undefined
      items: [
        {
          item_name: product.nameLong || undefined, //product name
          item_id: product.code || undefined, // product id - it should be the same as you have in google merchant center and facebook catalog feed
          price: formatPriceWithoutCurrency(
            product?.specialOfferPricing?.priceGross?.value ||
              product?.pricing?.priceGross?.value ||
              0
          ), // 1 item price (with applied discount - if exist)
          discount: product.specialOfferPricing
            ? formatPriceWithoutCurrency(
                product?.pricing?.priceGross?.value ||
                  0 - product?.specialOfferPricing?.priceGross?.value ||
                  0
              )
            : 0, // The monetary discount value associated with the 1 item. - if no discount 0
          currency:
            product?.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined, //currency
          item_brand: product?.brand?.name || undefined, // product manufacturer
          item_promotion: product.specialOfferPricing ? 'yes' : 'no', // yes if promotion applied, no if no promotion
          item_available: product.isAvailable ? 'yes' : 'no', // yes if item available, no if not
          item_new: isNewProduct(product.tags || []) ? 'yes' : 'no', // yes if item new (nowość) in shop, no if not new product
          item_adult_only:
            product.ageLimit && product.ageLimit >= 18 ? 'yes' : 'no', // yes if item for clients 18+, no if for everyone
          index: idxOnList || 0, //product position on the list - starts from 0
          ...getProductCategoriesForSeo(product), //Category path corresponding to the one on the page - if there are more steps in path add another item_category2 etc. (MAX number item_category5)
          item_variant: getProductUnitDetails(product) || undefined, //variant of the product
          item_list_name: listName, //list name where product displayed
          quantity: 1,
          creative_name: promotion?.creativeName || undefined,
          promotion_id: promotion?.promotionId || undefined
        }
      ]
    }
  }

  queueAnalyticsEvent(eventData)
}

export const getProductCategoriesForSeo = (product: ProductTypes) => {
  const categories = isNotNilOrEmpty(product.categoriesTree)
    ? product.categoriesTree
    : isNotNilOrEmpty(product.productCategories)
      ? product.productCategories
      : []

  const productCategories = {}

  if (isNotNilOrEmpty(categories)) {
    categories
      ?.slice(0, 5)
      ?.sort((a, b) => b.depth - a.depth)
      ?.forEach((cat, index) => {
        productCategories[`item_category${index + 1}`.replace('1', '')] =
          cat.name
      })
  }

  return productCategories
}

export const collectItems = (
  products: ProductTypes[],
  listName: SeoListName
): Ga4Item[] => {
  const SeoPromotionData = SeoPromotion.get()

  const items: Ga4Item[] = products?.map((product, idx) => ({
    item_name: product.nameLong || undefined,
    item_id: product.code || undefined,
    price: formatPriceWithoutCurrency(
      product?.specialOfferPricing?.priceGross?.value ||
        product?.pricing?.priceGross?.value ||
        0
    ),
    discount: product?.specialOfferPricing
      ? formatPriceWithoutCurrency(
          product?.pricing?.priceGross?.value ||
            0 - product?.specialOfferPricing?.priceGross?.value ||
            0
        )
      : 0,
    currency: product.pricing?.priceGross?.symbol === 'zł' ? 'PLN' : undefined,
    item_brand: product.brand?.name || undefined,
    item_promotion: product.specialOfferPricing ? 'yes' : 'no',
    item_available: product.isAvailable ? 'yes' : 'no',
    item_new: isNewProduct(product.tags) ? 'yes' : 'no',
    item_adult_only: product.ageLimit && product.ageLimit >= 18 ? 'yes' : 'no',
    index: idx,
    ...getProductCategoriesForSeo(product),
    item_variant: getProductUnitDetails(product) || undefined,
    item_list_name: listName,
    quantity: 1,
    creative_name: SeoPromotionData?.creativeName || undefined,
    promotion_id: SeoPromotionData?.promotionId || undefined
  }))

  return items
}
